import { useState, type FormEvent } from 'react';
import { api } from '@/api/client';
import type { Session } from '@/api/client';
import { useAuth } from '@/lib/auth';

/**
 * Blocks the rest of the console until a user whose password was set by someone else (an admin
 * creating or resetting their account) picks their own — see AuthController.changePassword.
 */
export function ChangePasswordPage() {
  const { session, setSession, signOut } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 12
    && newPassword === confirmPassword && !busy;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<Session>('/api/auth/change-password', {
        currentPassword, newPassword,
      });
      setSession(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-ink-100 p-6">
      <form onSubmit={onSubmit} className="card w-full max-w-sm p-6">
        <h1 className="text-lg font-semibold text-ink-900">Set your password</h1>
        <p className="mt-1 text-sm text-ink-500">
          {session?.fullName ? `${session.fullName}, your` : 'Your'} password was set for you —
          pick one only you know before continuing.
        </p>

        <label className="mt-5 block">
          <span className="label">Current password</span>
          <input
            className="input mt-1"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoFocus
            required
          />
        </label>

        <label className="mt-3 block">
          <span className="label">New password</span>
          <input
            className="input mt-1"
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-ink-500">At least 12 characters.</p>
        </label>

        <label className="mt-3 block">
          <span className="label">Confirm new password</span>
          <input
            className="input mt-1"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {mismatch && <p className="mt-1 text-xs text-red-600">Passwords don't match.</p>}
        </label>

        {error && (
          <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary mt-5 w-full justify-center" disabled={!canSubmit}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
        <button type="button" className="mt-3 w-full text-center text-xs text-ink-500 hover:text-ink-800"
                onClick={() => void signOut()}>
          Sign out instead
        </button>
      </form>
    </div>
  );
}
